import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import {
  cleanReloadMarker,
  isNewBuild,
  readBuildId,
  reloadUrlForBuild,
} from './appUpdate';

declare const __SORTEIA_BUILD_ID__: string;

const CHECK_INTERVAL_MS = 60_000;
const FIRST_CHECK_DELAY_MS = 15_000;

export function AppUpdatePrompt() {
  const language = useI18nStore((state) => state.language);
  const t = useI18nStore((state) => state.t);
  const [availableBuild, setAvailableBuild] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const dismissedBuild = useRef<string | null>(null);
  const checking = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (checking.current || !navigator.onLine || document.visibilityState === 'hidden') return;
    checking.current = true;
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const nextBuild = readBuildId(await response.json());
      if (isNewBuild(__SORTEIA_BUILD_ID__, nextBuild) && dismissedBuild.current !== nextBuild) {
        setAvailableBuild(nextBuild);
      }
    } catch {
      // Version discovery must never interfere with login, betting or payments.
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    const cleanUrl = cleanReloadMarker(window.location.href);
    if (cleanUrl) window.history.replaceState(window.history.state, '', cleanUrl);

    const firstCheck = window.setTimeout(() => { void checkForUpdate(); }, FIRST_CHECK_DELAY_MS);
    const interval = window.setInterval(() => { void checkForUpdate(); }, CHECK_INTERVAL_MS);
    const checkVisible = () => {
      if (document.visibilityState === 'visible') void checkForUpdate();
    };
    const checkFocused = () => { void checkForUpdate(); };
    document.addEventListener('visibilitychange', checkVisible);
    window.addEventListener('focus', checkFocused);
    window.addEventListener('online', checkFocused);
    return () => {
      window.clearTimeout(firstCheck);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', checkVisible);
      window.removeEventListener('focus', checkFocused);
      window.removeEventListener('online', checkFocused);
    };
  }, [checkForUpdate]);

  if (!availableBuild) return null;

  const refresh = () => {
    if (reloading) return;
    setReloading(true);
    window.location.replace(reloadUrlForBuild(window.location.href, availableBuild));
  };

  const dismiss = () => {
    dismissedBuild.current = availableBuild;
    setAvailableBuild(null);
  };

  return (
    <aside className="app-update-prompt" role="dialog" aria-live="polite" aria-label={t('appUpdateTitle')} lang={language} data-app-update-prompt="true">
      <span className="app-update-icon" aria-hidden="true"><RefreshCw size={20} /></span>
      <span className="app-update-copy">
        <strong>{t('appUpdateTitle')}</strong>
        <small>{t('appUpdateBody')}</small>
      </span>
      <span className="app-update-actions">
        <button className="app-update-now tap" type="button" disabled={reloading} onClick={refresh} autoFocus>
          {reloading ? t('appUpdateRefreshing') : t('appUpdateNow')}
        </button>
        <button className="app-update-later tap" type="button" disabled={reloading} onClick={dismiss}>{t('appUpdateLater')}</button>
      </span>
    </aside>
  );
}
